import { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDraggable } from '@dnd-kit/core';
import { getDevFlag } from '@frameio/shared/dev-flags';
import { useMeasure } from '@frameio/vapor';
import { theme } from '@frameio/vapor-theme';
import {
  ANIMATION_DURATION,
  BLOCK_LINE_HEIGHT_MAPPINGS,
  BLOCK_SIZE_MAPPINGS,
  MARQUEE_BLOCK_PADDING,
  MOTION_TYPES,
  TEXT_ALIGNMENTS,
} from '../../constants';
import type { WatermarkBlockData } from '../../types';
import { determineSection, getDisplayText } from '../../utils/builderUtils';

type WatermarkBlockProps = {
  block: WatermarkBlockData;
  containerWidth: number;
  containerHeight: number;
  isSelected: boolean;
  onSelect: (id: string) => void;
  scale: number;
  isReadOnly?: boolean;
};

export const WatermarkBlock: React.FC<WatermarkBlockProps> = ({
  block,
  containerWidth,
  containerHeight,
  isSelected,
  onSelect,
  scale,
  isReadOnly = false,
}) => {
  // Refs for block and content elements
  const blockRef = useRef<HTMLDivElement | null>(null);

  // Set up draggable functionality
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: block.id,
      data: {
        ...block,
        blockBoundingClient: blockRef.current?.getBoundingClientRect() || {},
      },
      disabled: isReadOnly,
    });

  const [marqueeContentRef, marqueeContentDimensions] = useMeasure();

  const isWMIDDebugModeEnabled = getDevFlag('ENABLE_WMID_DEBUG_MODE');

  const textAlignFlexMapping = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };

  // Scrolling text always renders left-aligned; otherwise it follows the
  // block's alignment (already normalized to center for rotated blocks by
  // resolveBlockAnchor upstream).
  const textAlign =
    block.motion !== MOTION_TYPES.NONE
      ? TEXT_ALIGNMENTS.LEFT
      : TEXT_ALIGNMENTS[block.textAlignment ?? 'CENTER'];

  // WMID debug mode only: derive the block's live center + section directly from
  // the current position/transform rather than mirroring it into state.
  let debugInfo = '';
  if (isWMIDDebugModeEnabled && blockRef.current) {
    const blockRect = blockRef.current.getBoundingClientRect();
    const center = {
      x:
        (block.position.x + (transform ? transform.x / scale : 0)) * scale +
        blockRect.width / 2,
      y:
        (block.position.y + (transform ? transform.y / scale : 0)) * scale +
        blockRect.height / 2,
    };

    const section = determineSection(
      center.x,
      center.y,
      containerWidth,
      containerHeight
    );
    debugInfo = `
      x: ${Math.round(block.position.x)}, y: ${Math.round(block.position.y)}
      xCenter: ${Math.round(center.x)}, yCenter: ${Math.round(
        center.y
      )}, Section: ${section}
    `;
  }

  // Handle block selection
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(block.id);
  };

  const displayText = getDisplayText(block);

  return (
    <BlockWrapper
      ref={(node) => {
        setNodeRef(node);
        blockRef.current = node;
      }}
      $readOnly={isReadOnly}
      data-selected={isSelected}
      data-testid="watermark-builder-block"
      id={block.id}
      style={{
        display: block.motion === 'none' ? 'flex' : undefined,
        left: `${block.position.x * scale}px`,
        top: `${block.position.y * scale}px`,
        width: block.motion === 'none' ? 'auto' : '100%',
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0) rotate(${block.rotation}deg)`
          : `rotate(${block.rotation}deg)`,
        color: block.color,
        boxSizing: 'border-box',
        border: `3px solid ${
          isSelected && !isReadOnly ? 'rgb(91, 83, 255)' : 'transparent'
        }`,
        cursor: block.motion === 'none' ? 'move' : 'ns-resize',
        fontSize: `${
          BLOCK_SIZE_MAPPINGS[block.size] * (scale * scale) // we square the scale to account for the increased size of the text
        }px`,
        fontWeight: theme.fontWeights.medium,
        lineHeight: `${
          BLOCK_LINE_HEIGHT_MAPPINGS[block.size] * scale * scale // we square the scale to account for the increased size of the text
        }px`,
        padding: '12px 18px',
        position: 'absolute',
        textAlign,
        textShadow: block.textShadow ? '1px 1px 2px black' : 'unset',
        userSelect: 'none',
        zIndex: isDragging ? 1000 : 1,
        alignItems:
          block.motion === 'none' ? textAlignFlexMapping[textAlign] : undefined,
        flexDirection: block.motion === 'none' ? 'column' : undefined,
        height: block.motion !== 'none' ? 'auto' : undefined,
        overflow: block.motion !== 'none' ? 'hidden' : undefined,
        whiteSpace: block.motion !== 'none' ? 'nowrap' : undefined,
      }}
      {...attributes}
      {...listeners}
      onClick={handleClick}
    >
      {isWMIDDebugModeEnabled && <DebugInfo>{debugInfo}</DebugInfo>}
      {block.motion === 'none' ? (
        displayText.map((text, index) => (
          <span
            key={index}
            style={{
              opacity: block.opacity / 100,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </span>
        ))
      ) : (
        <MarqueeContent
          ref={marqueeContentRef}
          $containerWidth={containerWidth}
          $duration={15}
          $marqueeContentWidth={marqueeContentDimensions.width}
          $motion={block.motion}
        >
          <span style={{ opacity: block.opacity / 100 }}>
            {displayText.join(' ')}
          </span>
        </MarqueeContent>
      )}
    </BlockWrapper>
  );
};

// Animation keyframes
const marqueeLeftToRight = (
  containerWidth: number,
  marqueeContentWidth: number
) => keyframes`
  from { transform: translateX(-${
    marqueeContentWidth + MARQUEE_BLOCK_PADDING
  }px); }
  to { transform: translateX(${containerWidth}px); }
`;

const marqueeRightToLeft = (
  containerWidth: number,
  marqueeContentWidth: number
) => keyframes`
  from { transform: translateX(${containerWidth - MARQUEE_BLOCK_PADDING}px); }
  to { transform: translateX(-${
    marqueeContentWidth + MARQUEE_BLOCK_PADDING
  }px); }
`;

// Marquee animation component
const MarqueeContent = styled.div<{
  $motion: string;
  $duration: number;
  $containerWidth: number;
  $marqueeContentWidth: number;
}>`
  animation: ${(props) =>
      props.$motion === MOTION_TYPES.LEFT_TO_RIGHT
        ? marqueeLeftToRight(props.$containerWidth, props.$marqueeContentWidth)
        : marqueeRightToLeft(props.$containerWidth, props.$marqueeContentWidth)}
    ${(props) =>
      (props.$containerWidth + props.$marqueeContentWidth) /
      ANIMATION_DURATION.MARQUEE}s
    linear infinite;
  display: inline-block;
`;

// Debug components
const DebugInfo = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 3px;
  color: white;
  font-size: ${theme.spaces[12]};
  left: 50%;
  padding: 2px 5px;
  position: absolute;
  top: -30px;
  transform: translateX(-50%);
  white-space: nowrap;
`;

const BlockWrapper = styled.div<{ $readOnly: boolean }>`
  transition: border-color 0.15s;
  ${({ $readOnly }) =>
    !$readOnly &&
    `&:hover {
      border: 3px solid rgb(91, 83, 255) !important;
    }`}
  ${({ $readOnly }) => $readOnly && `cursor: default !important;`}
`;
